// src/services/searchOptionLabels.js

/**
 * This file maps search option keys to user-friendly labels and underlying law IDs
 * It serves as the single source of truth for search option labeling
 */

// User-friendly labels for search options (what users see in results)
export const searchOptionLabels = {
    // ═══════════════════════════════════════════════════════════════════════
    // BASIC SEARCH OPTIONS
    // ═══════════════════════════════════════════════════════════════════════
    
    employmentProtection: 'Non-discrimination in Employment, Housing & Public Accommodation', 
    stateEmployees: 'Non-discrimination for State Employees', 
    relationship: 'Inclusive relationship & parental recognition', 
    healthcare: 'Inclusive healthcare laws & policies', 
    genderMarkers: 'Inclusive gender markers (driver\'s licenses, birth certificates)', 
    youthProtections: 'Inclusive youth protections', 
    criminalJustice: 'Inclusive criminal justice', 
    noBans: 'State does NOT ban cities/counties from passing non-discrimination laws', 
    noBathroom: 'State does NOT ban cities/counties from passing "bathroom laws"', 
    noExclusionarySexDefinition: 'State does NOT ban laws recognizing existence of transgender people', 
    noReligiousExemptions: 'State does NOT allow religious exemptions to equality', 
  
    // ═══════════════════════════════════════════════════════════════════════
    // ADVANCED SEARCH OPTIONS
    // ═══════════════════════════════════════════════════════════════════════
  
    // ─── Employment & Housing ───
    employmentNonDiscrimination: 'Employment Non-discrimination', // Law 9: "Employment Non-Discrimination"
    housingNonDiscrimination: 'Housing Non-discrimination', // Law 10: "Housing Non-Discrimination"
    publicAccommodationNonDiscrimination: 'Public Accommodation Non-discrimination', // Law 11: "Public Accommodation Non-Discrimination"
    stateEmployeeNonDiscrimination: 'Non-discrimination for State Employees', // Law 13: "Non-Discrimination Policies for State Employees"
    allowLocalLaws: 'State allows cities/counties to pass non-discrimination laws', // Law 14: "NEG. LAW: State Bans Cities and Counties from Passing Non-discrimination laws"
    noReligiousExemptionMarriage: 'State does NOT allow religious exemptions for marriage licenses', // Law 21: "NEG. LAW: State and Local Officials Providing Marriage Licenses"
  
    // ─── Relationship & Parental Recognition ───
    secondParentAdoption: 'Second-parent adoption for unmarried couples', // Law 1: "Second Parent Adoption for Unmarried Couples"
    adoptionNonDiscrimination: 'Adoption non-discrimination protections for LGBTQ+ parents', // Law 4: "Adoption Nondiscrimination Protections for LGBTQ Parents"
    fosterCareNonDiscrimination: 'Foster care non-discrimination protections for LGBTQ+ parents', // Law 5: "Foster Care Nondiscrimination Protections for LGBTQ Parents"
    assistedReproductionRecognition: 'Recognition for parents using assisted reproduction', // Law 6: "Recognition for Parents Using Assisted Reproduction"
    lgbtInclusiveSpouseForLeave: 'LGBTQ+-inclusive definitions of spouse/partner for family medical leave', // Law 7: "LGBTQ-Inclusive Definitions for Spouse/Partner for Family Medical Leave"
    lgbtInclusiveChildrenForLeave: 'LGBTQ+-inclusive definitions of children/parents for family medical leave', // Law 8: "LGBTQ-Inclusive Definitions for Children/Parents for Family Medical Leave"
  
    // ─── Healthcare ───
    privateHealthInsuranceNonDiscrimination: 'Private health insurance non-discrimination laws', // Law 34: "Private Health Insurance Non-Discrimination"
    shieldLawsTransgenderCare: 'Shield laws protecting access to transgender health care', // Law 36: "Shield Laws Protecting Access to Transgender Health Care"
    healthInsuranceTransgenderCoverage: 'Health insurance providers banned from excluding transgender coverage', // Law 37: "Health Insurance Providers Banned from Excluding Coverage for Trans-Specific Care"
    medicaidTransgenderCoverage: 'State Medicaid coverage for transgender-specific care', // Law 38: "State Medicaid Policy Related to Coverage for Transgender People"
    stateEmployeeTransgenderBenefits: 'Transgender-inclusive health benefits for state employees', // Law 39: "Transgender Inclusive Health Benefits for State Employees"
    fertilityPrivateInsurance: 'Fertility treatment coverage in private insurance', // Law 42: "Fertility Treatment Coverage in Private Insurance"
    fertilityMedicaid: 'Fertility treatment coverage in Medicaid', // Law 43: "Fertility Treatment Coverage in Medicaid"
    fertilityPreservationPrivate: 'Fertility preservation coverage in private insurance', // Law 44: "Fertility Preservation Coverage in Private Insurance"
    fertilityPreservationMedicaid: 'Fertility preservation coverage in Medicaid', // Law 45: "Fertility Preservation Coverage in Medicaid"
    transgenderYouthCare: 'State allows transgender-specific care for youth', // Law 35: "NEG. LAW: Ban on Best Practice Medical Care for Transgender Youth"
    noReligiousExemptionMedical: 'State does NOT allow religious exemptions for medical professionals', // Law 19: "NEG. LAW: Targeted Religious Exemption for Medical Professionals"
  
    // ─── Gender Markers & Documentation ───
    driverLicenseGenderMarker: 'Ability to change gender marker on Driver\'s License', // Law 51: "Changing Gender Marker on Driver's Licenses"
    birthCertificateGenderMarker: 'Ability to change gender marker on Birth Certificate', // Law 52: "Changing Gender Marker on Birth Certificates"
    xOptionDriverLicense: 'Non-binary "X" option on Driver\'s Licenses', // Law 53: "X Option on Driver's Licenses"
    xOptionBirthCertificate: 'Non-binary "X" option on Birth Certificates', // Law 54: "X Option on Birth Certificates"
    nameChangeProcess: 'Streamlined name change process', // Law 55: "Name Change Process"
    bathroomAccess: 'Transgender people can use bathrooms consistent with gender identity', // Law 15: "NEG. LAW: State Bans Transgender People from Using Bathrooms or Facilities Consistent with Gender in Gov. Bldgs." 
  
    // ─── Student Protections & Education ───
    studentNonDiscrimination: 'Non-discrimination laws covering LGBTQ+ students', // Law 22: "Non-discrimination Laws and Policies Covering LGBTQ Students"
    allowLocalStudentProtections: 'State allows local schools to pass LGBTQ+ non-discrimination laws', // Law 24: "NEG.LAW: Laws Banning Local Schools and Districts from Passing LGBTQ Nondiscrim. and/or Bullying"
    antibullyingLaws: 'Anti-bullying laws covering LGBTQ+ students', // Law 23: "Anti-Bullying Laws and Policies Covering LGBTQ Students"
    allowLocalAntibullyingPolicies: 'State allows local schools to have LGBTQ+ anti-bullying policies', // Law 24: "NEG.LAW: Laws Banning Local Schools and Districts from Passing LGBTQ Nondiscrim. and/or Bullying"
    lgbtInclusiveCurriculum: 'State curriculum standards required to be LGBTQ+-inclusive', // Law 25: "State Curriculum Standards Required to be LGBTQ-inclusive"
    noParentalOptOut: 'State does NOT require parental opt-out for LGBTQ+-inclusive curricula', // Law 27: "NEG. LAW: State Law Requires Parental Notification of LGBTQ-Inclusive Parents to Opt Children Out"
    noForcedOuting: 'State does NOT force outing of transgender youth in schools', // Law 31: "NEG. LAW: Forced Outing of Transgender Youth in Schools"
    noDontSayGay: 'State does NOT have "Don\'t Say Gay" laws', // Law 26: "NEG. LAW: Dont Say Gay Law Barring or Restricting Educatiors from Discussing LGBTQ People or Issues"
    transgenderSportsParticipation: 'Transgender students can participate in sports consistent with gender identity', // Law 29: "NEG. LAW: State Law Prevents Trans Students from Participating in Sports Consist. with their Gender Identity"
    noReligiousExemptionChildWelfare: 'State does NOT allow religious exemptions for child welfare services', // Law 18: "NEG. LAW: Targeted Religious Exemption for Child Welfare Services"
    childWelfareSystemProtections: 'Protections for LGBTQ+ youth in child welfare system', // Law 33: "Protections for LGBTQ Youth in Child Welfare System"
    conversionTherapyBan: 'Conversion therapy ban covering LGBTQ+ youth', // Law 32: "Conversion Therapy Ban Covering LGBTQ Youth"

    // ─── Criminal Justice ───
    hateCrimeLaws: 'Hate crime laws covering LGBTQ+ people', // Law 46: "Hate Crime Law Covering LGBTQ People"
    noPanicDefense: 'Ban on "panic defense" in criminal cases', // Law 47: "Ban on Panic Defense"
    juryServiceNonDiscrimination: 'Jury service non-discrimination', // Law 48: "Jury Service Non-discrimination"
    noHIVCriminalization: 'State does NOT criminalize HIV status', // Law 49: "NEG. LAW: HIV Criminalization Law"
    noDragRestrictions: 'State does NOT restrict drag performances', // Law 50: "NEG. LAW: Restrictions on Drag Performances"
  
    // ─── Data Collection ───
    dataCollectionAdults: 'Data collection for LGBTQ+ adults', // Law 40: "Data Collections for LGBTQ Adults"
    dataCollectionYouth: 'Data collection for LGBTQ+ youth' // Law 41: "Data Collection for LGBTQ Youth"
};

// ═══════════════════════════════════════════════════════════════════════
// SEARCH OPTION → LAW ID MAPPINGS WITH INLINE LAW NAMES
// ═══════════════════════════════════════════════════════════════════════

export const searchOptionToLaws = {
    // ─── BASIC SEARCH OPTIONS ───
    employmentProtection: [9, 10, 11], // Law 9: "Employment Non-Discrimination", Law 10: "Housing Non-Discrimination", Law 11: "Public Accommodation Non-Discrimination"
    stateEmployees: [13], // Law 13: "Non-Discrimination Policies for State Employees"
    relationship: [3, 5, 7], // Law 3: "Voluntary Acknowledgment of Parentage", Law 5: "Foster Care Nondiscrimination Protections for LGBTQ Parents", Law 7: "LGBTQ-Inclusive Definitions for Spouse/Partner for Family Medical Leave"
    healthcare: [34, 36, 37, 38, 39, 42, 43, 44, 45], // Law 34: "Private Health Insurance Non-Discrimination", Law 36: "Shield Laws Protecting Access to Transgender Health Care", Law 37: "Health Insurance Providers Banned from Excluding Coverage for Trans-Specific Care", Law 38: "State Medicaid Policy Related to Coverage for Transgender People", Law 39: "Transgender Inclusive Health Benefits for State Employees", Law 42-45: "Fertility Treatment and Preservation Coverage"
    genderMarkers: [51, 52, 53, 54, 55], // Law 51: "Changing Gender Marker on Driver's Licenses", Law 52: "Changing Gender Marker on Birth Certificates", Law 53: "X Option on Driver's Licenses", Law 54: "X Option on Birth Certificates", Law 55: "Name Change Process"
    youthProtections: [22, 23, 32, 33], // Law 22: "Non-discrimination Laws and Policies Covering LGBTQ Students", Law 23: "Anti-Bullying Laws and Policies Covering LGBTQ Students", Law 32: "Conversion Therapy Ban Covering LGBTQ Youth", Law 33: "Protections for LGBTQ Youth in Child Welfare System"
    criminalJustice: [46, 47, 48], // Law 46: "Hate Crime Law Covering LGBTQ People", Law 47: "Ban on Panic Defense", Law 48: "Jury Service Non-discrimination"
    
    // NEGATIVE LAWS (what states do NOT have)
    noBans: [14], // Law 14: "NEG. LAW: State Bans Cities and Counties from Passing Non-discrimination laws"
    noBathroom: [15], // Law 15: "NEG. LAW: State Bans Transgender People from Using Bathrooms or Facilities Consistent with Gender in Gov. Bldgs."
    noExclusionarySexDefinition: [16], // Law 16: "NEG. LAW: State Explicitly Defines Sex to Exclude Transgender People"
    noReligiousExemptions: [17, 18, 19, 20, 21], // Law 17: "NEG. LAW: Broad RFRA Law", Law 18: "NEG. LAW: Targeted Religious Exemption for Child Welfare Services", Law 19: "NEG. LAW: Targeted Religious Exemption for Medical Professionals", Law 20: "NEG. LAW: Targeted Religious Exemption for Wedding Services", Law 21: "NEG. LAW: State and Local Officials Providing Marriage Licenses"

    // ─── ADVANCED SEARCH OPTIONS ───
    employmentNonDiscrimination: [9], // Law 9: "Employment Non-Discrimination"
    housingNonDiscrimination: [10], // Law 10: "Housing Non-Discrimination"
    publicAccommodationNonDiscrimination: [11], // Law 11: "Public Accommodation Non-Discrimination"
    stateEmployeeNonDiscrimination: [13], // Law 13: "Non-Discrimination Policies for State Employees"
    allowLocalLaws: [14], // Law 14: "NEG. LAW: State Bans Cities and Counties from Passing Non-discrimination laws"
    noReligiousExemptionMarriage: [21], // Law 21: "NEG. LAW: State and Local Officials Providing Marriage Licenses"
    
    secondParentAdoption: [1], // Law 1: "Second Parent Adoption for Unmarried Couples"
    adoptionNonDiscrimination: [4], // Law 4: "Adoption Nondiscrimination Protections for LGBTQ Parents"
    fosterCareNonDiscrimination: [5], // Law 5: "Foster Care Nondiscrimination Protections for LGBTQ Parents"
    assistedReproductionRecognition: [6], // Law 6: "Recognition for Parents Using Assisted Reproduction"
    lgbtInclusiveSpouseForLeave: [7], // Law 7: "LGBTQ-Inclusive Definitions for Spouse/Partner for Family Medical Leave"
    lgbtInclusiveChildrenForLeave: [8], // Law 8: "LGBTQ-Inclusive Definitions for Children/Parents for Family Medical Leave"
    
    privateHealthInsuranceNonDiscrimination: [34], // Law 34: "Private Health Insurance Non-Discrimination"
    shieldLawsTransgenderCare: [36], // Law 36: "Shield Laws Protecting Access to Transgender Health Care"
    healthInsuranceTransgenderCoverage: [37], // Law 37: "Health Insurance Providers Banned from Excluding Coverage for Trans-Specific Care"
    medicaidTransgenderCoverage: [38], // Law 38: "State Medicaid Policy Related to Coverage for Transgender People"
    stateEmployeeTransgenderBenefits: [39], // Law 39: "Transgender Inclusive Health Benefits for State Employees"
    fertilityPrivateInsurance: [42], // Law 42: "Fertility Treatment Coverage in Private Insurance"
    fertilityMedicaid: [43], // Law 43: "Fertility Treatment Coverage in Medicaid"
    fertilityPreservationPrivate: [44], // Law 44: "Fertility Preservation Coverage in Private Insurance"
    fertilityPreservationMedicaid: [45], // Law 45: "Fertility Preservation Coverage in Medicaid"
    transgenderYouthCare: [35], // Law 35: "NEG. LAW: Ban on Best Practice Medical Care for Transgender Youth"
    noReligiousExemptionMedical: [19], // Law 19: "NEG. LAW: Targeted Religious Exemption for Medical Professionals"
    
    driverLicenseGenderMarker: [51], // Law 51: "Changing Gender Marker on Driver's Licenses"
    birthCertificateGenderMarker: [52], // Law 52: "Changing Gender Marker on Birth Certificates"
    xOptionDriverLicense: [53], // Law 53: "X Option on Driver's Licenses"
    xOptionBirthCertificate: [54], // Law 54: "X Option on Birth Certificates"
    nameChangeProcess: [55], // Law 55: "Name Change Process"
    
    studentNonDiscrimination: [22], // Law 22: "Non-discrimination Laws and Policies Covering LGBTQ Students"
    allowLocalStudentProtections: [24], // Law 24: "NEG.LAW: Laws Banning Local Schools and Districts from Passing LGBTQ Nondiscrim. and/or Bullying"
    antibullyingLaws: [23], // Law 23: "Anti-Bullying Laws and Policies Covering LGBTQ Students"
    allowLocalAntibullyingPolicies: [24], // Law 24: "NEG.LAW: Laws Banning Local Schools and Districts from Passing LGBTQ Nondiscrim. and/or Bullying"
    lgbtInclusiveCurriculum: [25], // Law 25: "State Curriculum Standards Required to be LGBTQ-inclusive"
    noParentalOptOut: [27], // Law 27: "NEG. LAW: State Law Requires Parental Notification of LGBTQ-Inclusive Parents to Opt Children Out"
    noForcedOuting: [31], // Law 31: "NEG. LAW: Forced Outing of Transgender Youth in Schools"
    noDontSayGay: [26], // Law 26: "NEG. LAW: Dont Say Gay Law Barring or Restricting Educatiors from Discussing LGBTQ People or Issues"
    transgenderSportsParticipation: [29], // Law 29: "NEG. LAW: State Law Prevents Trans Students from Participating in Sports Consist. with their Gender Identity"
    noReligiousExemptionChildWelfare: [18], // Law 18: "NEG. LAW: Targeted Religious Exemption for Child Welfare Services"
    childWelfareSystemProtections: [33], // Law 33: "Protections for LGBTQ Youth in Child Welfare System"
    conversionTherapyBan: [32], // Law 32: "Conversion Therapy Ban Covering LGBTQ Youth"
    
    hateCrimeLaws: [46], // Law 46: "Hate Crime Law Covering LGBTQ People"
    noPanicDefense: [47], // Law 47: "Ban on Panic Defense"
    juryServiceNonDiscrimination: [48], // Law 48: "Jury Service Non-discrimination"
    noHIVCriminalization: [49], // Law 49: "NEG. LAW: HIV Criminalization Law"
    noDragRestrictions: [50], // Law 50: "NEG. LAW: Restrictions on Drag Performances"
    
    dataCollectionAdults: [40], // Law 40: "Data Collections for LGBTQ Adults"
    dataCollectionYouth: [41], // Law 41: "Data Collection for LGBTQ Youth"
    
    bathroomAccess: [15] // Law 15: "NEG. LAW: State Bans Transgender People from Using Bathrooms or Facilities Consistent with Gender in Gov. Bldgs."
};

// ═══════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════

// Organize search options by category for better results display
export const searchOptionCategories = {
    'Employment & Housing Protections': [
        'employmentProtection',
        'employmentNonDiscrimination', 
        'housingNonDiscrimination',
        'publicAccommodationNonDiscrimination',
        'stateEmployees',
        'stateEmployeeNonDiscrimination',
        'allowLocalLaws'
    ],
    'Relationship & Family Recognition': [
        'relationship',
        'secondParentAdoption',
        'adoptionNonDiscrimination',
        'fosterCareNonDiscrimination',
        'assistedReproductionRecognition',
        'lgbtInclusiveSpouseForLeave',
        'lgbtInclusiveChildrenForLeave'
    ],
    'Healthcare Access & Coverage': [
        'healthcare',
        'privateHealthInsuranceNonDiscrimination',
        'shieldLawsTransgenderCare',
        'healthInsuranceTransgenderCoverage',
        'medicaidTransgenderCoverage',
        'stateEmployeeTransgenderBenefits',
        'fertilityPrivateInsurance',
        'fertilityMedicaid',
        'fertilityPreservationPrivate',
        'fertilityPreservationMedicaid',
        'transgenderYouthCare'
    ],
    'Gender Identity & Documentation': [
        'genderMarkers',
        'driverLicenseGenderMarker',
        'birthCertificateGenderMarker',
        'xOptionDriverLicense',
        'xOptionBirthCertificate',
        'nameChangeProcess',
        'bathroomAccess',
        'noExclusionarySexDefinition'
    ],
    'Youth & Education Protections': [
        'youthProtections',
        'studentNonDiscrimination',
        'allowLocalStudentProtections',
        'antibullyingLaws',
        'allowLocalAntibullyingPolicies',
        'lgbtInclusiveCurriculum',
        'noParentalOptOut',
        'noForcedOuting',
        'noDontSayGay',
        'transgenderSportsParticipation',
        'childWelfareSystemProtections',
        'conversionTherapyBan'
    ],
    'Criminal Justice & Safety': [
        'criminalJustice',
        'hateCrimeLaws',
        'noPanicDefense',
        'juryServiceNonDiscrimination',
        'noHIVCriminalization',
        'noDragRestrictions'
    ],
    'Religious Freedom & Local Laws': [
        'noBans',
        'noBathroom', 
        'noReligiousExemptions',
        'noReligiousExemptionMarriage',
        'noReligiousExemptionMedical',
        'noReligiousExemptionChildWelfare'
    ],
    'Data & Research': [
        'dataCollectionAdults',
        'dataCollectionYouth'
    ]
};

// Helper function to get search option label by key
export const getSearchOptionLabel = (searchOptionKey) => {
    return searchOptionLabels[searchOptionKey] || searchOptionKey;
};

// Helper function to get category for a search option
export const getSearchOptionCategory = (searchOptionKey) => {
    for (const [category, options] of Object.entries(searchOptionCategories)) {
        if (options.includes(searchOptionKey)) {
            return category;
        }
    }
    return 'Other';
};

// Helper function to check if a search option has negative law logic
export const isNegativeLaw = (searchOptionKey) => {
    return searchOptionKey.startsWith('no') || searchOptionKey.startsWith('allow');
};

export default {
    searchOptionLabels,
    searchOptionToLaws,
    searchOptionCategories,
    getSearchOptionLabel,
    getSearchOptionCategory,
    isNegativeLaw
};